// components/dashboard/StatCard.jsx
import React, { useState, useEffect } from 'react';
import { Card, Badge, Skeleton } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import CountUp from 'react-countup';
import { motion } from 'framer-motion';

const StatCard = ({
  title,
  value,
  prefix,
  suffix,
  icon,
  color = '#2563eb',
  trend,
  trendValue,
  loading = false,
  precision = 0,
  formatter,
  extra,
  delay = 0
}) => {
  const [prevValue, setPrevValue] = useState(0);
  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    if (!isFirstRender) {
      setPrevValue(value);
    }
    setIsFirstRender(false);
  }, [value]);

  const renderTrend = () => {
    if (!trend || !trendValue) return null;

    const isPositive = trend === 'up';
    const trendColor = isPositive ? '#059669' : '#dc2626';
    const TrendIcon = isPositive ? ArrowUpOutlined : ArrowDownOutlined;

    return (
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: delay + 0.3, duration: 0.3 }}
        style={{ marginTop: 8, fontSize: 12, color: trendColor }}
      >
        <TrendIcon style={{ marginRight: 4 }} />
        <span>{trendValue}</span>
      </motion.div>
    );
  };

  const renderValue = () => {
    if (loading) {
      return <Skeleton.Input active size="default" style={{ width: 80 }} />;
    }

    // Use formatter if provided
    if (formatter) {
      return (
        <span className="count-up-value">
          {prefix}{formatter(value)}{suffix}
        </span>
      );
    }

    // Use CountUp for numeric values
    return (
      <span className="count-up-value">
        {prefix}
        <CountUp
          start={isFirstRender ? 0 : prevValue}
          end={value || 0}
          duration={1.5}
          decimals={precision}
          separator=","
          delay={delay}
          useEasing={true}
          easingFn={(t, b, c, d) => {
            // Custom easing - ease out expo
            return c * (-Math.pow(2, -10 * t / d) + 1) + b;
          }}
        />
        {suffix}
      </span>
    );
  };

  const cardContent = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: delay,
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
    >
      <Card
        className="stat-card-enhanced"
        style={{
          borderTop: `3px solid ${color}`,
          height: '100%',
          borderRadius: 8,
          overflow: 'hidden',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}
        bodyStyle={{ padding: '20px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>
            {/* Title */}
            <div style={{
              color: '#475569',
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 8,
              letterSpacing: '0.1px'
            }}>
              {loading ? (
                <Skeleton.Input active size="small" style={{ width: 100 }} />
              ) : (
                title
              )}
            </div>

            {/* Value with CountUp */}
            <div style={{
              color: '#0f172a',
              fontSize: 26,
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: '-0.3px'
            }}>
              {renderValue()}
            </div>

            {/* Trend */}
            {renderTrend()}
          </div>

          {/* Icon */}
          {icon && (
            <motion.div
              className="stat-icon"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                delay: delay + 0.2,
                duration: 0.3,
                type: "spring",
                stiffness: 200
              }}
              style={{
                fontSize: 40,
                color: color,
                opacity: 0.85,
                marginLeft: 16
              }}
            >
              {icon}
            </motion.div>
          )}
        </div>
      </Card>
    </motion.div>
  );

  if (extra) {
    return (
      <Badge.Ribbon text={extra} color="blue">
        {cardContent}
      </Badge.Ribbon>
    );
  }

  return cardContent;
};

// Skeleton version for loading state
export const StatCardSkeleton = () => (
  <Card
    style={{
      borderTop: '3px solid #e1eaef',
      height: '100%',
      borderRadius: 8,
      border: '1px solid #e2e8f0',
      boxShadow: 'none',
    }}
    bodyStyle={{ padding: '20px' }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ flex: 1 }}>
        <Skeleton.Input active size="small" style={{ width: 100, marginBottom: 12 }} />
        <Skeleton.Input active size="large" style={{ width: 80 }} />
        <Skeleton.Input active size="small" style={{ width: 50, marginTop: 8 }} />
      </div>
      <Skeleton.Avatar active size={48} shape="circle" />
    </div>
  </Card>
);

export default StatCard;
